/**
 * 안전한 식 계산 — eval · Function 을 쓰지 않는다. 허용하는 것만 읽는다:
 *   숫자 · $value · $params.<이름> · $vars.<이름>(파생 값 · 동작 순서의 변수) · + − × ÷ % · 비교(< <= > >= == != → 1/0) · 괄호 · 단항 −
 *   함수 min max round floor ceil abs clamp if(조건, 참, 거짓) sqrt sin cos tan atan asin acos atan2(y, x)(도) hash(n)(0~1 결정적 무작위)
 *   $profile.<이름> = 운영 프로필(박스 · 팔레트 규격 — 트윈의 운영 설정) · $flow.<이름> = 흐름 연동 상태(동작 · 보이기에서만)
 * 예: "$params.trays" · "$value + 0.35" · "$params.height / 2" · "max(1, round($params.trays / 3))"
 * 식이 틀리면 compile 이 오류 문구를 돌려준다(검사기가 경로와 함께 보여 준다).
 */

type Node =
  | { k: "num"; v: number }
  | { k: "value" }
  | { k: "param"; name: string }
  | { k: "var"; name: string }
  | { k: "ivar"; name: string; idx: Node }
  | { k: "prof"; name: ProfileKey }
  | { k: "flow"; name: FlowKey }
  | { k: "cmp"; op: "<" | "<=" | ">" | ">=" | "==" | "!="; a: Node; b: Node }
  | { k: "neg"; a: Node }
  | { k: "bin"; op: "+" | "-" | "*" | "/" | "%"; a: Node; b: Node }
  | { k: "call"; fn: FnName; args: Node[] };

const FNS = {
  min: (xs: number[]) => Math.min(...xs),
  max: (xs: number[]) => Math.max(...xs),
  round: (xs: number[]) => Math.round(xs[0]),
  floor: (xs: number[]) => Math.floor(xs[0]),
  ceil: (xs: number[]) => Math.ceil(xs[0]),
  abs: (xs: number[]) => Math.abs(xs[0]),
  clamp: (xs: number[]) => Math.min(xs[2], Math.max(xs[1], xs[0])),
  if: (xs: number[]) => (xs[0] ? xs[1] : xs[2]),
  sqrt: (xs: number[]) => Math.sqrt(xs[0]),
  sin: (xs: number[]) => Math.sin((xs[0] * Math.PI) / 180),
  cos: (xs: number[]) => Math.cos((xs[0] * Math.PI) / 180),
  tan: (xs: number[]) => Math.tan((xs[0] * Math.PI) / 180),
  atan: (xs: number[]) => (Math.atan(xs[0]) * 180) / Math.PI,
  asin: (xs: number[]) => (Math.asin(xs[0]) * 180) / Math.PI,
  acos: (xs: number[]) => (Math.acos(xs[0]) * 180) / Math.PI,
  atan2: (xs: number[]) => (Math.atan2(xs[0], xs[1]) * 180) / Math.PI,
  /** 결정적 무작위 0~1 — 트윈 코드의 (k × 2654435761 mod 1000) / 1000 과 같은 규칙(정수로 반올림) */
  /** 칸 재고 비율 0~1(흐름 연동 · 칸 순서) — 실제 값은 run 에서 env.stock 으로(연동이 없으면 1 = 가득) */
  stock: () => 1,
  stockAvg: () => 1,
  hash: (xs: number[]) => ((Math.round(xs[0]) * 2654435761) % 1000 + 1000) % 1000 / 1000,
  /** [화물 수량] 처리 중 k 번째 화물(오래된 것부터 0) 번호 · 진행률 0~1 — 없으면 −1(실제 값은 run 에서 env.items) */
  fitem: () => -1,
  fprog: () => -1,
  /** [화물 수량] 화물마다 정수 단위 수(평균 = per — 3.5 면 화물마다 3 · 4 · 번호로 정해 늘 같음) — 코드 shared/unitFill unitsFor 와 같은 규칙 */
  units: (xs: number[]) => { const p = Math.max(1, Number.isFinite(xs[0]) ? xs[0] : 1); const b = Math.floor(p); const f = p - b; if (f < 1e-6) return b; const h = ((Math.imul(Math.round(xs[1]) | 0, 2654435761) >>> 0) % 10007) / 10007; return b + (h < f ? 1 : 0); },
  /** 구간 안 진행(k 가 s → e 일 때 0 → 1 · 밖이면 0 또는 1) */
  seg: (xs: number[]) => (xs[0] <= xs[1] ? 0 : xs[0] >= xs[2] ? 1 : (xs[0] - xs[1]) / (xs[2] - xs[1])),
  /** [화물 수량] 번호 → 0 ~ n−1 자리(결정적 · 고르게) — 코드 shared/unitFill hashPick 과 같은 규칙(목적지 칸 · 도어 고르기) */
  hpick: (xs: number[]) => { const n = Math.round(xs[1]); return n <= 1 ? 0 : ((Math.imul(Math.round(xs[0]) | 0, 2654435761) >>> 0) % 100003) % n; },
  /** [화물 수량] 들어와 서서 나가기 — 진행 k 가 [0, a] 면 p0 → p1 · [a, b] 면 p1 · [b, 1] 면 p1 → p2(좌표 한 축 · 입구 → 작업 자리 → 출구) */
  stopgo: (xs: number[]) => { const [k, a, b, p0, p1, p2] = xs; return k < a ? p0 + (p1 - p0) * (a > 0 ? Math.max(0, k) / a : 1) : k < b ? p1 : p1 + (p2 - p1) * (b < 1 ? Math.min(1, (k - b) / (1 - b)) : 1); },
  /** [화물 수량] 처리 중 k 번째 화물의 자리(0 ~ n−1 · 한 번 맡은 자리는 끝날 때까지 그대로 — 코드 slotAssigner) · 없으면 −1(실제 값은 run 에서 env.slot) */
  fslot: () => -1,
  /** [화물 수량] 진행률 a 이상 b 미만인 처리 중 화물 수 — (n, c) 를 주면 hpick(번호, n) = c 인 것만(칸마다 쌓인 수) */
  fcount: () => 0,
  /** [화물 수량 STEP 5c] 자리 s(0 ~ n−1)를 맡은 처리 중 화물 중 가장 오래된 것의 순번 k(fitem · fprog 에 넣는 값) · 없으면 −1 — 운반체 s 가 지금 나르는 화물(코드 carrierBook lead) */
  fslotk: () => -1,
  /** [화물 수량 STEP 5c] 처리 중 k 번째 화물이 자기 자리(fslot(k, n))에서 몇 번째인가 — 0 = 지금 맡은 것 · 1 이상 = 그 운반체 뒤에서 기다림 · 없으면 −1 */
  frank: () => -1,
};
type FnName = keyof typeof FNS;
const ARITY: Record<FnName, [number, number]> = { min: [1, 8], max: [1, 8], round: [1, 1], floor: [1, 1], ceil: [1, 1], abs: [1, 1], clamp: [3, 3], if: [3, 3], sqrt: [1, 1], sin: [1, 1], cos: [1, 1], tan: [1, 1], atan: [1, 1], asin: [1, 1], acos: [1, 1], atan2: [2, 2], hash: [1, 1], stock: [1, 1], stockAvg: [2, 2], fitem: [1, 1], fprog: [1, 1], units: [2, 2], seg: [3, 3], hpick: [2, 2], fslot: [2, 2], fcount: [2, 4], stopgo: [6, 6], fslotk: [2, 2], frank: [2, 2] };

/**
 * $profile — 트윈 운영 프로필(m · 1/0). 정의를 놓은 현장의 박스 · 팔레트 규격을 따른다(없으면 기본 프로필 값).
 *   boxW · boxD · boxH = 박스 폭 · 깊이 · 높이 · palletW · palletD = 팔레트 · loadH = 팔레트 적재 높이 · unitPallet · unitTote · unitCase · unitPiece = 운영 단위(1/0)
 */
export const PROFILE_DEFAULTS = { boxW: 0.5, boxD: 0.4, boxH: 0.34, palletW: 1.1, palletD: 1.1, loadH: 1.5, unitPallet: 0, unitTote: 0, unitCase: 1, unitPiece: 0 };
export type ProfileKey = keyof typeof PROFILE_DEFAULTS;
/**
 * $flow — 트윈 흐름 연동 상태(매 프레임). 동작(animations · sequences)과 part.visible 에서만 쓴다.
 *   coupled = 흐름에 묶였나(1/0, 갤러리 · 미리보기 = 0) · busy = 처리 중(연동이 없으면 1) · progress = 지금 화물 진행률 0~1(없으면 −1) · item = 화물 번호(없으면 −1)
 */
export const FLOW_DEFAULTS = { coupled: 0, busy: 1, progress: -1, item: -1 };
export type FlowKey = keyof typeof FLOW_DEFAULTS;

export interface Compiled { eval(env: ExprEnv): number; params: string[]; vars: string[]; usesValue: boolean; usesFlow: boolean; usesProfile: boolean }
export interface ExprEnv { value?: number; params: Record<string, unknown>; vars?: Record<string, number>; profile?: Partial<Record<ProfileKey, number>>; flow?: Partial<Record<FlowKey, number>>; stock?: number[] | null;
  /** [화물 수량] 처리 중 화물 전부(오래된 것부터) — fitem · fprog */
  items?: { id: number; progress: number }[] | null;
  /** [화물 수량] fslot(k, n) — 처리 중 k 번째 화물의 자리(build 가 프레임마다 slotAssigner 로) */
  slot?: ((k: number, n: number) => number) | null;
  /** [화물 수량 STEP 5c] fslotk(s, n) · frank(k, n) — 자리 s 의 맨 앞 화물 순번 · 화물 k 의 자리 안 차례(build 가 fslot 과 같은 나누기로) */
  slotLead?: ((s: number, n: number) => number) | null;
  slotRank?: ((k: number, n: number) => number) | null }

const MAX_LEN = 600;

export function compileExpr(src: string): { ok: true; expr: Compiled } | { ok: false; error: string } {
  if (src.length > MAX_LEN) return { ok: false, error: `식이 너무 깁니다(${MAX_LEN}자까지)` };
  let i = 0;
  const params = new Set<string>();
  const vars = new Set<string>();
  let usesValue = false; let usesFlow = false; let usesProfile = false;
  const ws = () => { while (i < src.length && /\s/.test(src[i])) i++; };
  const fail = (m: string): never => { throw new Error(`${m} (위치 ${i + 1})`); };

  const primary = (): Node => {
    ws();
    const c = src[i];
    if (c === "(") { i++; const n = compare(); ws(); if (src[i] !== ")") fail("닫는 괄호 ) 가 없습니다"); i++; return n; }
    if (c === "-") { i++; return { k: "neg", a: primary() }; }
    if (c === "+") { i++; return primary(); }
    if (c === "$") {
      const v = /^\$vars\.([A-Za-z_][A-Za-z0-9_]*)/.exec(src.slice(i));
      if (v) {
        i += v[0].length; vars.add(v[1]);
        if (src[i] === "[") { i++; const idx = compare(); ws(); if (src[i] !== "]") fail("닫는 ] 가 없습니다"); i++; return { k: "ivar", name: v[1], idx }; } // 번호 붙은 변수 c[식] → c_번호
        return { k: "var", name: v[1] };
      }
      const pf = /^\$(profile|flow)\.([A-Za-z_][A-Za-z0-9_]*)/.exec(src.slice(i));
      if (pf) {
        const table = pf[1] === "profile" ? PROFILE_DEFAULTS : FLOW_DEFAULTS;
        if (!Object.prototype.hasOwnProperty.call(table, pf[2])) fail(`$${pf[1]}.${pf[2]} — ${Object.keys(table).join(" · ")} 만`);
        i += pf[0].length;
        if (pf[1] === "profile") { usesProfile = true; return { k: "prof", name: pf[2] as ProfileKey }; }
        usesFlow = true; return { k: "flow", name: pf[2] as FlowKey };
      }
      const m = /^\$(value\b|params\.([A-Za-z_][A-Za-z0-9_]*))/.exec(src.slice(i));
      if (!m) fail("$value · $params.<이름> · $vars.<이름> · $profile.<이름> · $flow.<이름> 만 쓸 수 있습니다");
      i += m![0].length;
      if (m![1].startsWith("value")) { usesValue = true; return { k: "value" }; }
      params.add(m![2]);
      return { k: "param", name: m![2] };
    }
    const num = /^(\d+(\.\d+)?|\.\d+)([eE][+-]?\d+)?/.exec(src.slice(i));
    if (num) { i += num[0].length; return { k: "num", v: Number(num[0]) }; }
    const id = /^[A-Za-z_][A-Za-z0-9_]*/.exec(src.slice(i)); // atan2 처럼 숫자가 붙은 이름
    if (id) {
      const name = id[0] as FnName;
      if (!Object.prototype.hasOwnProperty.call(FNS, name)) fail(`모르는 이름 「${id[0]}」 — 함수는 ${Object.keys(FNS).join(", ")} 만`);
      i += id[0].length; ws();
      if (src[i] !== "(") fail(`${name} 뒤에 ( 가 필요합니다`);
      i++;
      const args: Node[] = [];
      ws();
      if (src[i] !== ")") { for (;;) { args.push(compare()); ws(); if (src[i] === ",") { i++; continue; } break; } }
      if (src[i] !== ")") fail("닫는 괄호 ) 가 없습니다");
      i++;
      if (name === "stock" || name === "stockAvg" || name === "fitem" || name === "fprog" || name === "fslot" || name === "fcount" || name === "fslotk" || name === "frank") usesFlow = true; // 칸 재고 · 처리 중 화물은 흐름 연동 값
      const [lo, hi] = ARITY[name];
      if (args.length < lo || args.length > hi) fail(`${name} 는 값 ${lo === hi ? lo : `${lo}~${hi}`}개를 받습니다`);
      return { k: "call", fn: name, args };
    }
    return fail(c === undefined ? "식이 중간에 끝났습니다" : `「${c}」 는 쓸 수 없습니다`);
  };
  const product = (): Node => {
    let a = primary();
    for (;;) { ws(); const c = src[i]; if (c === "*" || c === "/" || c === "%") { i++; a = { k: "bin", op: c, a, b: primary() }; } else return a; }
  };
  const sum = (): Node => {
    let a = product();
    for (;;) { ws(); const c = src[i]; if (c === "+" || c === "-") { i++; a = { k: "bin", op: c, a, b: product() }; } else return a; }
  };
  /** 비교 — 가장 낮은 우선순위(참 1 · 거짓 0) */
  const compare = (): Node => {
    const a = sum(); ws();
    const m = /^(<=|>=|==|!=|<|>)/.exec(src.slice(i));
    if (!m) return a;
    i += m[0].length;
    return { k: "cmp", op: m[0] as "<" | "<=" | ">" | ">=" | "==" | "!=", a, b: sum() };
  };

  try {
    const root = compare();
    ws();
    if (i < src.length) fail(`「${src[i]}」 는 쓸 수 없습니다`);
    const run = (n: Node, env: ExprEnv): number => {
      switch (n.k) {
        case "num": return n.v;
        case "value": return Number(env.value);
        case "param": { const v = env.params[n.name]; return typeof v === "boolean" ? (v ? 1 : 0) : Number(v); }
        case "var": return env.vars && n.name in env.vars ? Number(env.vars[n.name]) : NaN;
        case "ivar": { const key = `${n.name}_${Math.round(run(n.idx, env))}`; return env.vars && key in env.vars ? Number(env.vars[key]) : NaN; }
        case "prof": { const v = env.profile && env.profile[n.name]; return typeof v === "number" ? v : PROFILE_DEFAULTS[n.name]; }
        case "flow": { const v = env.flow && env.flow[n.name]; return typeof v === "number" ? v : FLOW_DEFAULTS[n.name]; }
        case "cmp": {
          const a = run(n.a, env); const b = run(n.b, env);
          const r = n.op === "<" ? a < b : n.op === "<=" ? a <= b : n.op === ">" ? a > b : n.op === ">=" ? a >= b : n.op === "==" ? Math.abs(a - b) < 1e-9 : !(Math.abs(a - b) < 1e-9); // != : 값이 없으면(NaN) 「다르다」(1)
          return r ? 1 : 0;
        }
        case "neg": return -run(n.a, env);
        case "bin": {
          const a = run(n.a, env); const b = run(n.b, env);
          return n.op === "+" ? a + b : n.op === "-" ? a - b : n.op === "*" ? a * b : n.op === "/" ? a / b : a % b;
        }
        case "call": {
          if (n.fn === "stockAvg") { // 칸 재고 평균(a ~ b 칸) — 3D 칸 하나가 재고 칸 여러 개일 때
            const a = Math.max(0, Math.round(run(n.args[0], env))); const b = Math.round(run(n.args[1], env)); const st = env.stock;
            if (!st || !st.length) return 1;
            let s2 = 0; let c = 0; for (let q = a; q <= Math.min(b, st.length - 1); q++) { s2 += st[q]; c++; }
            return c ? s2 / c : 1;
          }
          if (n.fn === "fitem" || n.fn === "fprog") { const k = Math.round(run(n.args[0], env)); const it = env.items && k >= 0 && k < env.items.length ? env.items[k] : null; return it ? (n.fn === "fitem" ? it.id : it.progress) : -1; }
          if (n.fn === "fslot") { const k = Math.round(run(n.args[0], env)); const m = Math.max(1, Math.round(run(n.args[1], env))); return env.slot && env.items && k >= 0 && k < env.items.length ? env.slot(k, m) : -1; }
          if (n.fn === "fslotk" || n.fn === "frank") {
            const a = Math.round(run(n.args[0], env)); const m = Math.max(1, Math.round(run(n.args[1], env)));
            const f = n.fn === "fslotk" ? env.slotLead : env.slotRank;
            return f && env.items && a >= 0 ? f(a, m) : -1;
          }
          if (n.fn === "fcount") {
            const a = run(n.args[0], env), b = run(n.args[1], env); const its = env.items; if (!its) return 0;
            const m = n.args.length >= 3 ? Math.round(run(n.args[2], env)) : 0; const c = n.args.length >= 4 ? Math.round(run(n.args[3], env)) : 0;
            let cnt = 0; for (const it of its) if (it.progress >= a && it.progress < b && (m <= 0 || FNS.hpick([it.id, m]) === c)) cnt++;
            return cnt;
          }
          if (n.fn === "stock") { const i = Math.round(run(n.args[0], env)); const st = env.stock; const v = st && i >= 0 && i < st.length ? st[i] : undefined; return typeof v === "number" ? v : 1; }
          return FNS[n.fn](n.args.map((x) => run(x, env)));
        }
        default: return NaN;
      }
    };
    return { ok: true, expr: { eval: (env) => run(root, env), params: Array.from(params), vars: Array.from(vars), usesValue, usesFlow, usesProfile } };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}

/** 숫자면 그대로, "$…" 식 문자열이면 계산 · 숫자 문자열("2.5")도 받는다. 계산이 안 되면 NaN */
// 같은 식을 매 프레임 다시 읽지 않게(동작 속도 등) — 정의 하나의 식 수는 수백 개 이하
const CACHE = new Map<string, Compiled | null>();
export function evalNum(v: unknown, env: ExprEnv): number {
  if (typeof v === "number") return v;
  if (typeof v === "boolean") return v ? 1 : 0;
  if (typeof v !== "string") return NaN;
  const key = v.trim();
  let c = CACHE.get(key);
  if (c === undefined) {
    const r = compileExpr(key);
    c = r.ok ? r.expr : null;
    if (CACHE.size >= 20000) { const first = CACHE.keys().next(); if (!first.done) CACHE.delete(first.value); } // 가장 오래된 것부터 하나씩(전부 비우면 큰 정의가 매번 다시 컴파일)
    CACHE.set(key, c);
  }
  return c ? c.eval(env) : NaN;
}
